import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { undoRedoData } from '../data/undoRedoData';
import { ArtistSection } from './ArtistSection';
import PaperDrawingBackground from './PaperDrawingBackground';
import { ExhibitionLayout } from './layouts/ExhibitionLayout';
import { ExhibitionNavigation } from './navigation/ExhibitionNavigation';
import styled from '@emotion/styled';

const IntroSection = styled.section`
  max-width: 800px;
  margin: 0 auto 2rem auto;
  padding: 0 0.5rem;
  text-align: center;

  @media (min-width: 640px) {
    margin-bottom: 3rem;
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    margin-bottom: 4rem;
  }
`;

const ConceptText = styled.p`
  color: #2c2a26;
  font-size: 0.9rem;
  line-height: 1.7;
  margin-bottom: 0.875rem;

  @media (min-width: 640px) {
    font-size: 1rem;
    line-height: 1.8;
  }

  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
`;

const UndoRedo = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [activeArtist, setActiveArtist] = useState<string | null>(artistId || null);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (!artistId) return;
    const scrollToSection = () => {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
        return true;
      }
      return false;
    };
    if (!scrollToSection()) {
      setTimeout(scrollToSection, 100);
    }
  }, [artistId]);

  const scrollToArtist = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      setIsScrolling(true);
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveArtist(id);
      navigate(`/exhibitions/undo-redo/${id}`);
      setTimeout(() => setIsScrolling(false), 1000);
    }
  }, [navigate]);

  useEffect(() => {
    let scrollTimeout: number;

    const handleScroll = () => {
      if (isScrolling) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const sections = undoRedoData.map(artist => ({
          id: artist.id,
          element: document.getElementById(artist.id),
        }));

        const current = sections.find(section => {
          if (!section.element) return false;
          const rect = section.element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        });

        if (current && current.id !== activeArtist) {
          setActiveArtist(current.id);
          navigate(`/exhibitions/undo-redo/${current.id}`);
        }
      }, 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [navigate, isScrolling, activeArtist]);

  useEffect(() => {
    if (artistId && artistId !== activeArtist) {
      const element = document.getElementById(artistId);
      if (element) {
        setIsScrolling(true);
        element.scrollIntoView({ behavior: 'smooth' });
        setActiveArtist(artistId);
        setTimeout(() => setIsScrolling(false), 1000);
      }
    }
  }, [artistId, activeArtist]);

  const navigationItems = undoRedoData.map(artist => ({
    id: artist.id,
    name: artist.name,
  }));

  return (
    <ExhibitionLayout
      title="Undo Redo"
      subtitle="Exploring fragile states of memory, choice, and alternate timelines"
      openingDate="FEBRUARY 2026"
      closingDate="APRIL 2026"
      background={<PaperDrawingBackground />}
      variant="paper"
      navigation={
        <ExhibitionNavigation
          items={navigationItems}
          activeId={activeArtist}
          onItemClick={scrollToArtist}
          basePath="/exhibitions/undo-redo"
          variant="paper"
        />
      }
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 0.5rem' }}>
        <IntroSection>
          <ConceptText>
            Undo · Redo explores the fragile space between what we remember and what we imagine.
            In alternate timelines, the path we took was different—or ignored.
            The work considers how memory, choice, and the decisions that arise invite us to re-examine
            the moments that shape us.
          </ConceptText>
          <ConceptText>
            We invite artists Jood Jung, Ignat Martynov, Chae Sengkwong, Christine Cho,
            Jessica Chen, and Kotaro Yokomizo to explore these themes with us.
          </ConceptText>
        </IntroSection>

        {undoRedoData.map(artist => (
          <ArtistSection key={artist.id} artist={artist} variant="paper" />
        ))}
      </div>
    </ExhibitionLayout>
  );
};

export default UndoRedo;
